<template>
  <div class="grid-x full-width admin-page-container">
    <div class="cell small-12 medium-10 medium-offset-1 large-10 large-offset-1 content-section">
      <!-- Dark Glassmorphic Top Container -->
      <div class="glass-card">
        <!-- Header & Action Row -->
        <div class="header-action-row grid-x align-middle">
          <div class="cell small-12 medium-12 text-left">
            <h1 class="page-title">User Management</h1>
            <span class="subtitle">System directory & access control.</span>
          </div>

          <!-- Search Filter Input -->
          <div class="cell small-12 medium-4 search-cell">
            <div class="search-input-wrapper">
              <svg
                class="search-icon-svg"
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input v-model="searchQuery" type="text" placeholder="Search by name or email..." class="search-field" />
              <button v-if="searchQuery" class="clear-search" title="Clear search" @click="searchQuery = ''">
                <i class="fa-solid fa-xmark"></i>
              </button>
            </div>
          </div>

          <div class="cell small-12 medium-5 text-right action-buttons">
            <button class="btn-refresh" :disabled="loading" @click="refreshUsers">
              <i class="fa-solid fa-rotate" :class="{ 'fa-spin': loading }"></i>
              <span class="btn-text-wrapper">
                <transition name="fade-slide" mode="out-in">
                  <span :key="loading" class="btn-text">
                    {{ loading ? 'Syncing...' : 'Refresh' }}
                  </span>
                </transition>
              </span>
            </button>

            <button class="btn-add-user" @click="openAddModal">
              <i class="fa-solid fa-user-plus" style="margin-right: 0.4rem;"></i> Add New User
            </button>
          </div>
        </div>

        <!-- Quick Stats Overview Bar -->
        <div class="stats-row grid-x">
          <div class="cell small-4 stat-card">
            <span class="stat-label">Total Users</span>
            <span class="stat-value">{{ users.length }}</span>
          </div>
          <div class="cell small-4 stat-card">
            <span class="stat-label">Active Today</span>
            <span class="stat-value text-success">{{ activeTodayCount }}</span>
          </div>
          <div class="cell small-4 stat-card">
            <span class="stat-label">Total Logins</span>
            <span class="stat-value text-accent">{{ totalLoginsCount }}</span>
          </div>
        </div>

        <!-- Main User Table Container -->
        <UserTable
          :users="filteredUsers"
          :loading="loading"
          :search-query="searchQuery"
          @edit="openEditModal"
          @clear-search="searchQuery = ''"
        />
      </div>
    </div>
  </div>

  <!-- Modal Component Call -->
  <UserModal
    :is-open="showModal"
    :is-editing="isEditing"
    :user-data="selectedUser"
    :saving="saving"
    @close="closeModal"
    @save="saveUser"
  />
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useUserDirectory } from '@/composables/useUserDirectory';
import UserModal from '@/components/admin/UserModal.vue';
import UserTable from '@/components/admin/UserTable.vue';

const { users, loading, fetchUsers, saveUser: saveUserApi } = useUserDirectory();

const showModal = ref(false);
const isEditing = ref(false);
const saving = ref(false);
const searchQuery = ref('');
const selectedUser = ref(null);

const defaultUserForm = () => ({
  id: '',
  name: '',
  email: '',
  picture: '',
  role: 'User',
  password: '',
  firstname: '',
  lastname: '',
  location: { address_line1: '', address_line2: '', address_line3: '', country: '', postcode: '' }
});

const filteredUsers = computed(() => {
  if (!searchQuery.value.trim()) return users.value;
  const query = searchQuery.value.toLowerCase();
  return users.value.filter(u =>
    (u.name && u.name.toLowerCase().includes(query)) ||
    (u.email && u.email.toLowerCase().includes(query))
  );
});

const activeTodayCount = computed(() => {
  const today = new Date().toDateString();
  return users.value.filter(u => u.lastLogin && new Date(u.lastLogin).toDateString() === today).length;
});

const totalLoginsCount = computed(() => {
  return users.value.reduce((acc, u) => acc + (Number(u.logins) || 0), 0);
});

const refreshUsers = () => fetchUsers();

const openAddModal = () => {
  isEditing.value = false;
  selectedUser.value = defaultUserForm();
  showModal.value = true;
};

const openEditModal = (user) => {
  isEditing.value = true;
  selectedUser.value = user;
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
};

const saveUser = async (formData) => {
  saving.value = true;
  try {
    await saveUserApi(formData, isEditing.value);
    closeModal();
    refreshUsers();
  } catch (err) {
    console.error('Save User Failed:', err);
    alert('Error saving user: ' + err.message);
  } finally {
    saving.value = false;
  }
};

onMounted(() => {
  fetchUsers();
});
</script>

<style lang="scss" scoped>
@use "sass:color";
@use "@/assets/styles/abstract/variables" as *;

$bg-dark: #0b0f19;
$card-dark: rgba(17, 24, 39, 0.75);
$border-dark: rgba(255, 255, 255, 0.08);
$accent-cyan: #38bdf8;
$text-light: #f8fafc;
$text-muted: #94a3b8;

h1.page-title {
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.01em;
  line-height: 1.2;
  white-space: nowrap;
  text-align: left;
}

.subtitle {
  color: $text-light;
  opacity: 0.7;
  display: block;
  margin-top: 0.1rem;
}

.admin-page-container {
  padding-top: 1.5rem;
  padding-bottom: 3rem;

  .glass-card {
    background: $card-dark;
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border: 1px solid $border-dark;
    border-radius: 18px;
    padding: 1.75rem 2rem;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  }

  .header-action-row {
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 1rem;

    .search-cell {
      flex: 1 1 100%;
      max-width: 100%;
      padding: 0;
      display: flex;
      align-items: center;

      @media (min-width: 769px) {
        flex: 1 1 auto;
        max-width: 400px;
      }

      .search-input-wrapper {
        position: relative;
        display: flex;
        align-items: center;
        width: 100%;
        background: rgba(15, 23, 42, 0.9);
        border: 1px solid $border-dark;
        border-radius: 9px;
        padding: 0.45rem 0.8rem;
        transition: all 0.2s ease;
        box-sizing: border-box;

        &:focus-within {
          border-color: $accent-cyan;
          background: rgba(15, 23, 42, 1);
          box-shadow: 0 0 0 3px rgba(56, 189, 248, 0.15);
        }

        .search-icon-svg {
          display: flex;
          align-items: center;
          justify-content: center;
          color: $text-muted;
          pointer-events: none;
          margin-right: 0.6rem;
          flex-shrink: 0;
        }

        .search-field {
          flex: 1;
          width: 100%;
          background: transparent;
          border: none;
          padding: 0;
          margin: 0;
          font-size: 0.85rem;
          line-height: 1.5;
          color: $text-light;

          &::placeholder {
            color: rgba(148, 163, 184, 0.5);
          }

          &:focus {
            outline: none;
            box-shadow: none;
          }
        }

        .clear-search {
          background: none;
          border: none;
          font-size: 0.8rem;
          color: $text-muted;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0.2rem;
          margin-left: 0.5rem;
          flex-shrink: 0;
          line-height: 1;

          &:hover {
            color: $text-light;
          }
        }
      }
    }

    .action-buttons {
      flex: 1 1 auto;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 0.5rem;

      @media screen and (max-width: 768px) {
        justify-content: center;
        width: 100%;
        margin-top: 0.5rem;
      }

      button {
        border-radius: 8px;
        padding: 0.55rem 1rem;
        font-size: 0.82rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        white-space: nowrap;
      }

      .btn-refresh {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        background: rgba(255, 255, 255, 0.04);
        color: $text-muted;
        border: 1px solid $border-dark;

        .btn-text-wrapper {
          display: inline-block;
          position: relative;
          min-width: 52px;
          text-align: center;
        }

        .fade-slide-enter-active,
        .fade-slide-leave-active {
          transition: all 0.15s ease;
        }

        .fade-slide-enter-from {
          opacity: 0;
          transform: translateY(4px);
        }

        .fade-slide-leave-to {
          opacity: 0;
          transform: translateY(-4px);
        }

        &:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.08);
          color: $text-light;
          border-color: rgba(255, 255, 255, 0.15);
        }
      }

      .btn-add-user {
        background: $accent-cyan;
        color: #0f172a;
        border: 1px solid transparent;
        font-weight: 700;

        &:hover {
          background: color.adjust($accent-cyan, $lightness: 6%);
          box-shadow: 0 0 15px rgba(56, 189, 248, 0.4);
        }
      }
    }
  }

  /* Quick Stats Row Cards */
  .stats-row {
    margin-bottom: 1.25rem;
    gap: 0;

    .stat-card {
      background: rgba(15, 23, 42, 0.6);
      border: 1px solid $border-dark;
      padding: 0.75rem 1rem;
      border-radius: 10px;
      margin-right: 0.75rem;
      flex: 1;
      display: flex;
      flex-direction: column;

      &:last-child {
        margin-right: 0;
      }

      .stat-label {
        font-size: 0.7rem;
        font-weight: 700;
        color: $text-muted;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      .stat-value {
        font-size: 1.25rem;
        font-weight: 800;
        color: $text-light;
        margin-top: 0.1rem;

        &.text-success {
          color: #34d399;
        }

        &.text-accent {
          color: $orange;
        }
      }
    }
  }
}
</style>
